import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  RoundOutcome,
  RoundReveal,
  getRoundOutcome,
  getRoundReveal,
} from "../api/reveal";
import { RealtimeEvent, usePlayerLive, usePlayerRealtime } from "./usePlayerRealtime";
import { serverNow } from "../utils/serverClock";

/**
 * Drives the reveal popup.
 *
 * When a round stops taking bets the player is shown a modal that counts the
 * round down, opens its numbers one position at a time, and then announces what
 * their own slip did. This hook owns that sequence; the dialog that renders it
 * owns no timing of its own.
 *
 * Three sources feed it, and they are belt, braces and a spare:
 *   • the `round.reveal` / `round.outcome` events — what makes it immediate;
 *   • the `round-reveal` / `my-round-outcome` live subscriptions — what makes it
 *     correct after a reconnect, since a subscription is answered as it opens;
 *   • a one-off fetch when the popup opens — the reconnect fallback, for the
 *     case where the socket is down entirely.
 *
 * All three carry the same payload, so they are merged by simply taking the
 * newest rather than reconciled field by field.
 *
 * Nothing here decides anything about the round. The phase, the numbers, the
 * outcome and even the pace the numbers open at all come from the server — a
 * client that ran its own clock would show two tabs of the same round drawing
 * at different moments.
 */

/** What the dialog renders. Everything it needs, nothing it has to derive. */
export interface RevealState {
  open: boolean;
  reveal: RoundReveal | null;
  outcome: RoundOutcome | null;
  /**
   * The numbers opened so far — the dialog renders `revealed.length` of them
   * and leaves the rest as empty slots.
   */
  revealed: number[];
  /** True once every number has landed, which is the cue for the result card. */
  numbersComplete: boolean;
  /** Seconds to the draw, recomputed locally against the server's `closesAt`. */
  secondsToDraw: number;
  dismiss: () => void;
}

const secondsUntil = (iso: string | undefined) => {
  if (!iso) return 0;
  const target = Date.parse(iso);
  if (!Number.isFinite(target)) return 0;
  return Math.max(0, Math.ceil((target - serverNow()) / 1000));
};

/** The phases that put the popup on screen. */
const SHOWING: RoundReveal["phase"][] = ["lockdown", "drawing", "result", "settled"];

/**
 * How much slower than the server's pace the numbers open, by design.
 *
 * The server sets the beat; this stretches it so each number's pop has room to
 * finish before the next one starts. It touches the deal only — the countdown
 * and every request still run on the server's own clock.
 */
const NUMBER_PACE = 1.3;

/**
 * How long the result card stays up before the popup closes itself.
 *
 * Fixed here rather than taken from the server's `outcomeHoldMs`: win or lose,
 * the player is reading a slip, and the server's beat is set for the draw.
 */
const OUTCOME_HOLD_MS = 8_000;

/**
 * The same hold for a player with no slip on the round.
 *
 * There is no result card for them to read — just the numbers they have
 * already watched land — so the popup gets out of the way instead of sitting
 * on a finished round.
 */
const NO_SLIP_HOLD_MS = 1_500;

/**
 * How long after its own draw a round stops being the popup's business.
 *
 * The pin is what stops the next round hijacking a ceremony in flight, and it
 * is normally released by the ceremony ending. But the frames that end it come
 * over the socket: miss them — a drop, a round that settles with no numbers —
 * and the pin would be held for good, filtering out every later round and
 * leaving the popup dead until the page was reloaded. This is the backstop, set
 * well past the longest honest ceremony (deal, then the outcome hold).
 */
const PIN_RELEASE_AFTER_DRAW_MS = 45_000;

export const useRoundReveal = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const [reveal, setReveal] = useState<RoundReveal | null>(null);
  const [outcome, setOutcome] = useState<RoundOutcome | null>(null);
  const [revealed, setRevealed] = useState<number[]>([]);
  const [secondsToDraw, setSecondsToDraw] = useState(0);
  /**
   * The round this popup is following.
   *
   * Pinned the moment it opens, and the subscriptions are re-pointed at it. The
   * next round opens the instant this one settles, so a popup still following
   * "whatever is current" would finish its animation showing a round the player
   * never saw the numbers for.
   */
  const [pinned, setPinned] = useState<number | null>(null);
  /** Rounds the player has closed. Dismissing must not be undone by the next frame. */
  const dismissed = useRef(new Set<number>());

  const isCurrentRound = useCallback(
    (drawNo: number) => pinned === null || drawNo === pinned,
    [pinned]
  );

  /* ── Taking a frame, from whichever source produced it ─────────────── */

  const applyReveal = useCallback(
    (next: RoundReveal) => {
      if (!next?.drawNo || !isCurrentRound(next.drawNo)) return;
      if (dismissed.current.has(next.drawNo)) return;

      setReveal((previous) => {
        // Numbers arriving for the first time restart the deal; a later frame
        // for the same round must not replay it.
        if (!previous || previous.drawNo !== next.drawNo) setRevealed([]);
        else if (next.results.length && !previous.results.length) setRevealed([]);
        return next;
      });

      if (SHOWING.includes(next.phase)) {
        setPinned((previous) => previous ?? next.drawNo);
      } else {
        // `betting` or `void`: there is nothing to show. Unpin so the popup
        // follows the next round rather than staying stuck on this one.
        setPinned(null);
        setOutcome(null);
      }
    },
    [isCurrentRound]
  );

  const applyOutcome = useCallback(
    (next: RoundOutcome) => {
      if (!next?.drawNo || !isCurrentRound(next.drawNo)) return;
      if (dismissed.current.has(next.drawNo)) return;
      setOutcome(next);
    },
    [isCurrentRound]
  );

  /* ── Source one: the pushed events ────────────────────────────────── */

  const onEvent = useCallback(
    (event: RealtimeEvent) => {
      if (!enabled) return;
      if (event.event === "round.reveal") applyReveal(event.data as unknown as RoundReveal);
      if (event.event === "round.outcome") applyOutcome(event.data as unknown as RoundOutcome);
    },
    [applyOutcome, applyReveal, enabled]
  );

  usePlayerRealtime(onEvent);

  /* ── Source two: the live subscriptions ───────────────────────────── */

  const params = useMemo(() => (pinned === null ? {} : { drawNo: pinned }), [pinned]);

  usePlayerLive<RoundReveal>("round-reveal", params, applyReveal, { enabled });
  usePlayerLive<RoundOutcome>("my-round-outcome", params, applyOutcome, { enabled });

  /* ── Source three: one fetch when the popup opens ─────────────────── */

  /**
   * The ceremony is the round's, so everyone watching gets the countdown and
   * the numbers. Only the result card is the player's own, and the dialog
   * gates that on their slip.
   */
  const open = pinned !== null && !!reveal && SHOWING.includes(reveal.phase);

  useEffect(() => {
    if (!enabled || pinned === null) return;
    let cancelled = false;

    // The socket has almost certainly delivered this already — but if it is
    // down, this is the only thing that puts the popup on screen at all.
    getRoundReveal(pinned)
      .then((fresh) => {
        if (!cancelled) applyReveal(fresh);
      })
      .catch(() => undefined);
    getRoundOutcome(pinned)
      .then((fresh) => {
        if (!cancelled) applyOutcome(fresh);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [applyOutcome, applyReveal, enabled, pinned]);

  /* ── The countdown ────────────────────────────────────────────────── */

  useEffect(() => {
    if (!open || !reveal) return;
    const tick = () => setSecondsToDraw(secondsUntil(reveal.closesAt));
    tick();
    const timer = window.setInterval(tick, 250);
    // A throttled background tab wakes with a stale clock; read it on return
    // rather than waiting for the next tick.
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [open, reveal]);

  /* ── Opening the numbers, at the server's pace ────────────────────── */

  useEffect(() => {
    if (!open || !reveal?.results.length) return;
    if (revealed.length >= reveal.results.length) return;

    const step = (reveal.animation?.numberIntervalMs ?? 700) * NUMBER_PACE;
    const timer = window.setTimeout(() => {
      setRevealed((previous) =>
        previous.length >= reveal.results.length
          ? previous
          : reveal.results.slice(0, previous.length + 1)
      );
    }, step);

    return () => window.clearTimeout(timer);
  }, [open, reveal, revealed.length]);

  /* ── Closing ──────────────────────────────────────────────────────── */

  const dismiss = useCallback(() => {
    if (reveal) dismissed.current.add(reveal.drawNo);
    setPinned(null);
    setOutcome(null);
    setRevealed([]);
  }, [reveal]);

  /* The backstop fires on the round's clock, so it must not be rescheduled
     every time `dismiss` is rebuilt. */
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    dismissRef.current = dismiss;
  }, [dismiss]);

  const closesAt = reveal?.closesAt;

  /**
   * Closes itself once the result has had its moment.
   *
   * Only after settlement, and only after the numbers have finished opening —
   * a popup that closed on the timer while the last number was still landing
   * would take the answer off screen before the player read it.
   */
  const numbersComplete =
    !!reveal?.results.length && revealed.length >= reveal.results.length;
  const finished = open && reveal?.phase === "settled" && numbersComplete;

  /* Shortened only on a confirmed empty slip: an outcome still in flight is
     not "no bets", and closing early on one would cut a result card short. */
  const satOut = !!outcome && outcome.drawNo === reveal?.drawNo && !outcome.hasBets;

  useEffect(() => {
    if (!finished || !reveal) return;
    const timer = window.setTimeout(dismiss, satOut ? NO_SLIP_HOLD_MS : OUTCOME_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [dismiss, finished, reveal, satOut]);

  /**
   * The backstop on the pin.
   *
   * Scheduled against the round's own draw time rather than a lifetime from
   * now, so repeated frames for the same round cannot keep pushing it back.
   */
  useEffect(() => {
    if (pinned === null) return;
    const draw = Date.parse(closesAt ?? "");
    if (!Number.isFinite(draw)) return;
    const timer = window.setTimeout(
      () => dismissRef.current(),
      Math.max(0, draw + PIN_RELEASE_AFTER_DRAW_MS - serverNow())
    );
    return () => window.clearTimeout(timer);
  }, [closesAt, pinned]);

  /* Signing out mid-ceremony must not leave the previous player's card up. */
  useEffect(() => {
    if (enabled) return;
    setPinned(null);
    setReveal(null);
    setOutcome(null);
    setRevealed([]);
  }, [enabled]);

  return {
    open,
    reveal,
    outcome,
    revealed,
    numbersComplete,
    secondsToDraw,
    dismiss,
  } satisfies RevealState;
};
